import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DashboardTopSearch from "../../DashboardTopSearch/DashboardTopSearch";
import { useNavigate } from "react-router-dom";
import "react-phone-number-input/style.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getEditProfileAPI,
  updateInstructorProfileAPI,
  updatePasswordAPI,
} from "../../../redux/features/adminSlice";
import toast from "react-hot-toast";
import { getLoginResponseAPI } from "../../../redux/features/authSlice";
import User from "../../../assets/images/edit-profile-thumbnail.svg";
import Notification from "../../Notification/Notification";
import AdminNotification from "../../Notification/AdminNotification";
import InstructorNotification from "../../Notification/InstructorNotification";
import PasswordChangeModal from "./passwordChangeModal/passwordChangeModal";
import CustomButton from "../../CustomButton/CustomButton";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Calendar } from "../../svgs/Svgs";


const EditProfile = () => {
  const [profileImage, setProfileImage] = useState();
  const navigate = useNavigate();
  const today = new Date();
  const [passwordShow, setPasswordShow] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState("us");

  const { loading, lastActionType } = useSelector((state) => state.admin);

  const [formInitialValues, setFormInitialValues] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    date_of_birth: "",
    password: "",
    plan: "",
    profileImage: null,
  });

  const fetchCountryFallback = async () => {
    try {
      const response = await fetch(
        "https://api.ipgeolocation.io/ipgeo?apiKey=a648d260fc9d43dfa2a8ec35e0572235"
      );
      const data = await response.json();
      if (data?.country_code2) {
        return data.country_code2.toLowerCase();
      }
    } catch (error) {
      console.error("Failed to fetch country from IP:", error);
    }
    return "us"; // Default fallback
  };

  useEffect(() => {
    const getFetchCall = async () => {
      const res = await dispatch(
        getEditProfileAPI({ token: auth.token, userId: searchParams.get("id") })
      ).unwrap();
      console.log("res", res);
      if (res.success) {
        const phoneNumberFull = res.data.phone_number || ""; // With "+" if present
        let phoneNumber = phoneNumberFull.replace(/^\+/, ""); // Without "+" for PhoneInput
        let countryFromPhone = "us"; // Default

        // Parse country from phone number if available
        if (phoneNumberFull.startsWith("+")) {
          try {
            const parsed = parsePhoneNumberFromString(phoneNumberFull);
            if (parsed && parsed.country) {
              countryFromPhone = parsed.country.toLowerCase();
            }
          } catch (error) {
            console.error("Failed to parse phone number:", error);
            // Fallback to IP if parsing fails
            countryFromPhone = await fetchCountryFallback();
          }
        } else {
          // Fallback to IP if no phone number
          countryFromPhone = await fetchCountryFallback();
        }

        setDefaultCountry(countryFromPhone);

        setFormInitialValues({
          full_name: res.data.full_name || "",
          email: res.data.email || "",
          phone_number: phoneNumber,
          date_of_birth: res.data.date_of_birth || "",
          password: "",
          plan: res.data?.UserProfile?.paymentPlan?.name || "",
          profileImage: res.data.profile_picture || null,
        });
        if (res.data.profile_picture) {
          setProfileImage(res.data.profile_picture);
        }
      }
    };

    getFetchCall();
  }, [auth.token, searchParams, dispatch]);

  const submitHandler = async (values) => {
    const res = await dispatch(
      updateInstructorProfileAPI({
        token: auth.token,
        userId: searchParams.get("id"),
        payload: values,
      })
    ).unwrap();
    if (res.success) {
      toast.success(res.message);
      await dispatch(getLoginResponseAPI({ userId: auth.user_id })).unwrap();

      if (searchParams.get("path") === "student-details") {
        navigate(`/student-details?id=${searchParams.get("id")}`);
      } else if (searchParams.get("path") === "instructor-details") {
        navigate(`/instructor-details?id=${searchParams.get("id")}`);
      } else if (searchParams.get("path") === "profile") {
        navigate(`/profile?id=${searchParams.get("id")}`);
      }
    }
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone_number: Yup.string()
      .required("Phone number is required")
      .test("is-valid-phone", "Invalid phone number", (value) => {
        if (!value) return false;
        const cleaned = value.replace(/\D/g, "");
        return cleaned.length >= 10 && cleaned.length <= 15;
      }),
    date_of_birth: Yup.date().required("Date of Birth is required"),
  });

  const handleImageChange = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
      setFieldValue("profileImage", file);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      {loading && lastActionType !== "updatePasswordAPI" && (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      )}
      <div className="topbar mb-20 mb-lg-36">
        <div className="d-flex align-items-center justify-content-between">
          <h3
            className="ad-heading-04 fw-bold black-800 me-29 mb-0 d-flex align-items-center gap-3 cursor-pointer"
            onClick={goBack}
          >
            <span className="icon-arrow-left"></span>Edit Profile
          </h3>
          <div className="search-wrapper d-none d-lg-flex align-items-center gap-3">
            <DashboardTopSearch />
            {auth.role === "Admin" ? (
              <AdminNotification />
            ) : auth.role === "Instructor" ? (
              <InstructorNotification />
            ) : auth.role === "Student" ? (
              <Notification />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>

      <div className="card-default-style px-10 px-sm-25 px-md-32 px-lg-42 py-10 py-sm-20 py-md-32">
        <div className="edit-profile-container">
          <Formik
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form>
                <div className="user-img-container rounded-circle position-relative mx-auto mb-49">
                  {profileImage?.includes("blob") ? (
                    <img
                      src={profileImage}
                      alt="User Profile"
                      className="img-fluid w-100 h-100 object-fit-cover p-4 rounded-circle"
                      onError={(e) => {
                        e.target.src = null;
                        e.target.src = User;
                      }}
                    />
                  ) : profileImage === undefined ? (
                    <img
                      src={User}
                      alt="User Profile"
                      className="img-fluid w-100 h-100 object-fit-cover p-4 rounded-circle"
                      onError={(e) => {
                        e.target.src = null;
                        e.target.src = User;
                      }}
                    />
                  ) : (
                    <img
                      src={process.env.REACT_APP_GALLERY_URL + profileImage}
                      alt="User Profile"
                      className="img-fluid w-100 h-100 object-fit-cover p-4 rounded-circle"
                      onError={(e) => {
                        e.target.src = null;
                        e.target.src = User;
                      }}
                    />
                  )}
                  <input
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    className="d-none"
                    id="profile-upload"
                    onChange={(e) => handleImageChange(e, setFieldValue)}
                  />
                  <label
                    htmlFor="profile-upload"
                    className="edit-profile-btn d-flex justify-content-center align-items-center aa-text-base"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.875 16H3.125C1.40184 16 0 14.5981 0 12.875V3.125C0 1.40188 1.40184 0 3.125 0H9.18847C9.53366 0 9.81347 0.279813 9.81347 0.625C9.81347 0.970187 9.53366 1.25 9.18847 1.25H3.125C2.09113 1.25 1.25 2.09113 1.25 3.125V12.875C1.25 13.9089 2.09113 14.75 3.125 14.75H12.875C13.9089 14.75 14.75 13.9089 14.75 12.875V6.81153C14.75 6.46634 15.0298 6.18653 15.375 6.18653C15.7202 6.18653 16 6.46634 16 6.81153V12.875C16 14.5981 14.5982 16 12.875 16Z"
                        fill="white"
                      />
                      <path
                        d="M14.9552 0.731063C13.9804 -0.243687 12.3944 -0.243687 11.4196 0.731063L4.61617 7.5345C3.47029 8.68034 2.97592 10.3131 3.29373 11.9022C3.3432 12.1496 3.53657 12.343 3.78398 12.3924C5.36539 12.7087 7.00036 12.2213 8.1517 11.07L14.9552 4.26656C15.9299 3.29184 15.9299 1.70581 14.9552 0.731063ZM14.0713 3.38272L7.26779 10.1862C6.52154 10.9324 5.49698 11.3059 4.45995 11.2262C4.3801 10.1895 4.7537 9.16469 5.50004 8.41838L10.7946 3.12381L11.2366 3.56578C11.4806 3.80984 11.8764 3.80991 12.1204 3.56578C12.3645 3.32172 12.3646 2.92597 12.1205 2.68191L11.6785 2.23994L12.3035 1.61494C12.7909 1.12756 13.5839 1.12756 14.0713 1.61494C14.5586 2.10231 14.5586 2.89534 14.0713 3.38272Z"
                        fill="white"
                      />
                    </svg>
                  </label>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="full_name"
                        className="form-label fw-semibold text-black-100"
                      >
                        Name
                      </label>
                      <Field
                        type="text"
                        id="full_name"
                        name="full_name"
                        className="form-control"
                        placeholder="Enter Your First Name"
                      />
                      <ErrorMessage
                        name="full_name"
                        component="div"
                        className="text-danger mt-5"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="email"
                        className="form-label fw-semibold text-black-100"
                      >
                        Email
                      </label>
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="Enter Your Email"
                        disabled
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger mt-5"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="phone_number"
                        className="form-label fw-semibold text-black-100"
                      >
                        Phone Number
                      </label>
                      <div>
                        <PhoneInput
                          country={defaultCountry}
                          value={values.phone_number}
                          onChange={(value) =>
                            setFieldValue(
                              "phone_number",
                              "+" + value.replace(/^\+/, "")
                            )
                          }
                          enableSearch={true}
                          countryCodeEditable={false}
                          inputProps={{
                            name: "phone_number",
                            required: true,
                            className: "form-control",
                          }}
                        />
                      </div>
                      <ErrorMessage
                        name="phone_number"
                        component="div"
                        className="text-danger error"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="date_of_birth"
                        className="form-label fw-semibold text-black-100"
                      >
                        Date of Birth
                      </label>
                      <DatePicker
                        selected={
                          values.date_of_birth
                            ? new Date(values.date_of_birth)
                            : null
                        }
                        onChange={(date) =>
                          setFieldValue(
                            "date_of_birth",
                            date ? format(date, "MM-dd-yyyy") : ""
                          )
                        }
                        maxDate={today}
                        dateFormat="MM-dd-yyyy"
                        className="form-control"
                        placeholderText="MM-DD-YYYY"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        onKeyDown={(e) => e.preventDefault()}
                        icon={<Calendar color="#000" />}
                        showIcon
                      />
                      <ErrorMessage
                        name="date_of_birth"
                        component="div"
                        className="text-danger mt-5"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="password"
                        className="form-label fw-semibold text-black-100"
                      >
                        Password
                      </label>
                      <div className="position-relative">
                        <Field
                          style={{ minHeight: "67px" }}
                          type={passwordShow ? "text" : "password"}
                          id="password"
                          name="password"
                          className="form-control"
                          placeholder="********"
                          disabled
                        />
                        <button
                          type="button"
                          className="btn btn-soft-green text-xs fw-semibold mt-2 top-50 end-0 position-absolute translate-middle-y no-shadow me-17 py-10"
                          onClick={() => setShowModal(true)}
                        >
                          Change Password
                        </button>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger mt-5"
                      />
                    </div>
                  </div>
                  {auth.role.toLowerCase() === "student" && (
                  <div className="col-md-6">
                    <div className="mb-16">
                      <label
                        htmlFor="plan"
                        className="form-label fw-semibold text-black-100"
                      >
                        Subscription Plan
                      </label>
                      <Field
                        style={{ minHeight: "67px" }}
                        type="text"
                        id="plan"
                        name="plan"
                        className="form-control"
                        placeholder="Standard"
                        disabled
                      />
                      <ErrorMessage
                        name="plan"
                        component="div"
                        className="text-danger mt-5"
                      />
                    </div>
                  </div>
                  )}
                  <div className="col-12 mt-32">
                    <CustomButton
                      type="submit"
                      label="Save"
                      disabled={isSubmitting}
                      isSubmitting={isSubmitting}
                      classes="btn btn-primary float-end px-50 no-shadow"
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <PasswordChangeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={updatePasswordAPI}
        auth={auth}
        dispatch={dispatch}
        searchParams={searchParams}
      />
    </>
  );
};

export default EditProfile;
